import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { contactSection } from "@/i18n/contactSection";
import { mediaUrl } from "@/lib/mediaUrl";
import { getWhatsAppUrl } from "@/lib/contact";

const ArtPanel = ({ src, side, testId }) => (
  <div
    aria-hidden="true"
    data-testid={testId}
    className={`absolute inset-y-0 hidden w-[21%] overflow-hidden xl:block ${
      side === "left" ? "left-0" : "right-0"
    }`}
  >
    <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
  </div>
);

const Contact = () => {
  const { t, lang } = useLanguage();
  const c = contactSection[lang];
  const reduce = useReducedMotion();

  const enter = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-70px" },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section id="contact" data-testid="contact-section" className="relative scroll-mt-20 py-20 md:py-28">
      <div
        aria-hidden="true"
        className="bg-diagonal-lines absolute inset-0 [mask-image:radial-gradient(72%_78%_at_50%_46%,black,transparent)]"
      />
        <ArtPanel src={mediaUrl("/art/side-left.jpg")} side="left" testId="contact-art-left" />
        <ArtPanel src={mediaUrl("/art/side-right.jpg")} side="right" testId="contact-art-right" />

      <div className="relative mx-auto max-w-2xl px-5 md:px-8">
        <motion.div {...enter(0)} className="interactive-card rounded-[1.75rem] border border-line bg-white p-8 shadow-card md:p-12">
          <p className="text-sm font-semibold text-graphite/45">{c.eyebrow}</p>
          <h2
            data-testid="contact-title"
            className="font-display mt-6 text-3xl font-bold text-charcoal sm:text-4xl lg:text-[2.9rem] lg:leading-[1.3]"
          >
            {t.contact.title}
          </h2>
          <p
            data-testid="contact-subtitle"
            className="mt-4 text-base leading-relaxed text-graphite/70 md:text-lg"
          >
            {t.contact.sub}
          </p>
          <div className="mt-9 grid gap-3">
            <a
              href={getWhatsAppUrl(c.whatsappMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-cta"
              className="flex min-h-[54px] items-center justify-center gap-2.5 rounded-2xl bg-[#16845B] px-4 text-center text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#106B49] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#16845B]"
            >
              <MessageCircle className="size-5 shrink-0" aria-hidden="true" />
              {c.whatsappCta}
            </a>
          </div>
        </motion.div>

        <motion.div {...enter(0.12)} className="interactive-card mt-6 rounded-[1.75rem] border border-line bg-white p-8 shadow-card md:p-10">
          <h3 className="text-sm font-bold text-graphite/55">{c.expectTitle}</h3>
          <ul className="mt-5 flex flex-col gap-4">
            {c.expectations.map((item, i) => (
              <li
                key={item}
                data-testid={`contact-expectation-${i}`}
                className="flex items-start gap-3 text-sm leading-relaxed text-graphite/80 md:text-base"
              >
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-sage-deep" strokeWidth={1.75} />
                {item}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
